import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';

interface BannerWithTooltipProps {
  text: string;
  tooltip?: string;
  color?: string;
  section: 'editItems' | 'editAddress' | 'reviewProofs' | 'replaceReturn';
  triggerRef?: React.MutableRefObject<() => void>;
  collapseId?: string;
}

const popupDescriptions: Record<string, Record<string, string>> = {
  editItems: {
    "Already with designers": "It looks like your order is already “With Designers” and, therefore, has reached a point in our system where your item(s) can no longer be edited.",
    "Already in production": "It looks like your order is already “In Production” and, therefore, has reached a point in our system where your item(s) can no longer be edited.",
    "Already shipped": "It looks like your order is already “Shipped” and, therefore, has reached a point in our system where your item(s) can no longer be edited.",
    "Already in quality review": "It looks like your order is already \"In Quality Review\" and, therefore, has reached a point in our system where your item(s) can no longer be edited.",
    "Already canceled": "It looks like your order has been canceled and, therefore, has reached a point in our system where your item(s) can no longer be edited.",
  },
  editAddress: {
    "Already in production": "It looks like your order is already “In Production” and, therefore, your shipping address can no longer be updated.",
    "Already in quality review": "It looks like your order is already \"In Quality Review\" for shipment and, therefore, your shipping address can no longer be updated.",
    "Already shipped": "It looks like your order has already “Shipped” and, therefore, your shipping address can no longer be updated.",
    "Already canceled": "It looks like your order has been canceled and, therefore, your shipping address can no longer be updated.",
  },
  reviewProofs: {
    "Already in production": "It looks like your order is already “In Production” and, therefore, has reached a point in our system where your design proof(s) can no longer be reviewed.",
    "Proofs not yet created": "It looks like your design proof(s) are not yet ready. You'll be notified when they become available.",
    "Revisions in progress": "It looks like revisions are already in progress for your design proof(s), and therefore, this section is temporarily locked while our team works on your updates.",
    "Already with designers": "It looks like your order is already with our design team, and therefore, your proof(s) are currently being prepared and not yet available for review.",
    "Already in quality review": "It looks like your order is already \"In Quality Review\" for shipment. Proof review is no longer available.",
    "Already shipped": "It looks like your order is already “Shipped”. Proof review is no longer available.",
    "Already canceled": "It looks like your order has been canceled and proof review is no longer available.",
  },
  replaceReturn: {
    "Not yet shipped": "It looks like your order has not yet been shipped and, therefore, is not currently eligible for a replacement/return.",
    "Tracking updated < 5 days ago": "It looks like your order has not yet been delivered or deemed lost in transit and, therefore, is not currently eligible for a replacement/return, per our store policy.",
    "Delivered > 7 days ago": "It looks like your order was delivered more than 7 days ago and, therefore, is no longer eligible for a replacement/return, per our store policy.",
    "Already in quality review": "Your order is already in quality review and cannot be returned.",
    "Already shipped": "Your order has already shipped and is beyond the return window.",
    "$": "A replacement/return request was previously approved. This section is not available.",
    "Pending": " It looks like a replacement/return request has already been submitted for your order and is currently pending. Therefore, this section is not available.",
    "Approved": " It looks like a replacement/return request was previously submitted and approved for your order. Therefore, this section is not currently available.",
    "Rejected": "It looks like a replacement/return request was previously submitted and rejected for your order. Check your email for the reason behind the rejection.",
  },
};

const BannerWithTooltip: React.FC<BannerWithTooltipProps> = ({ text, tooltip, color, section, triggerRef, collapseId }) => {
  const iconRef = useRef<SVGSVGElement>(null);
  const [showPopup, setShowPopup] = useState(false);
  const collapseSection = (collapseId: string) => {
    // Find the button that targets this collapseId
    const toggleButton = document.querySelector(
      `button[data-toggle="collapse"][data-target="#${collapseId}"]`
    ) as HTMLElement;

    if (toggleButton) {
      toggleButton.click(); // Simulate a real click
    }
  };

  useEffect(() => {
    if (iconRef.current) {
      iconRef.current.style.setProperty('color', '#ffffff', 'important');
    }
    // expose trigger method to parent
    if (triggerRef) {
      triggerRef.current = () => {
        if (collapseId) {
          // collapseSection(collapseId)
        } else {
          setShowPopup(true); // fallback to showing popup
        }
      };
    }
  }, [triggerRef]);

  const description = tooltip && popupDescriptions[section]?.[tooltip];

  return (
    <>
      <div className={`section-banner-text ${color || ''} flex items-center gap-2 cursor-pointer`}
        onClick={(e) => {
          const card = e.currentTarget.closest('.card');
          if (card?.classList.contains('disabled')) {
            setShowPopup(true); // show popup if disabled
          } else if (collapseId) {
            collapseSection(collapseId);
          }
        }}
      >
        <span>{text}</span>
      </div>

      {showPopup && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-box" onClick={(e) => e.stopPropagation()}>
            <div className="popup-icon">
            <FontAwesomeIcon icon={faCircleInfo} className="popup-info-icon" />
            </div>
            <div className="popup-title">Section Unavailable</div>
            <div className="popup-description">{description || "This section is not currently available."}</div>
            <button className="contact-btn bg-brand w-100" style={{minHeight: "50px", height: "50px"}} onClick={() => setShowPopup(false)}>Okay</button>
          </div>
        </div>
      )}
    </>
  );
};

export default BannerWithTooltip;
